<template>
  <div v-if="(isImpossibleTraveler ? impossibleTravelerRecords : records).length">
    <div class="full-details-wrapper">
      <v-data-table-server
        :items="isImpossibleTraveler ? impossibleTravelerRecords : records"
        :single-expand="singleExpand"
        v-model:expanded="expanded"
        hide-default-footer
        item-value="uniqueId"
        show-expand
        fixed-header
        :items-length="(isImpossibleTraveler ? impossibleTravelerRecords : records).length"
      >
        <template #headers>
          <tr>
            <th :aria-label="$t('ariaLabels.expanderColumn')"></th>
            <th class="text-left" data-testid="tickets-page-anomaly-detection-type">
              {{ $t("ticketDetails.fullDetails.anomalyDetection.type") }}
            </th>
            <th class="text-center" data-testid="tickets-page-anomaly-detection-time">
              {{ $t("ticketDetails.fullDetails.anomalyDetection.time") }}
            </th>
            <th class="text-center" data-testid="tickets-page-anomaly-detection-country-ip">
              {{ $t("ticketDetails.fullDetails.anomalyDetection.countryAndIp") }}
            </th>
          </tr>
        </template>
        <template #item="{ item, toggleExpand, isExpanded, internalItem, index }">
          <tr :id="`${index}`">
            <td class="w-10">
              <v-icon
                v-if="shouldHaveExpander(item)"
                class="ml-1 text-primary"
                icon="$dropdown"
                :class="{ rotated: isExpanded(internalItem) }"
                @click="toggleExpand(internalItem)"
              >
              </v-icon>
            </td>
            <td class="pa-4 pl-4 w-25">
              <div class="d-flex align-center">
                <div
                  class="inner-circle mr-4"
                  v-if="ticket.eventType !== TicketType.USER_INACTIVITY"
                  :style="getCircleStyle((item as AnomalyDetection).suspicious)"
                ></div>
                <div
                  class="subtitle2 word-break-break-word"
                  :data-testid="`tickets-page-anomaly-detection-type-row-${index}`"
                >
                  {{ $t(`ticketDetails.fullDetails.anomalyDetection.${item.type}`) }}
                </div>
              </div>
            </td>
            <td class="pa-4 w-25">
              <div class="d-flex flex-column justify-center align-center">
                <span
                  class="body2"
                  :data-testid="`tickets-page-anomaly-detection-date-row-${index}`"
                >
                  {{ getFormattedDateTime(item.originalTime, "ddd, MMM, DD") }}
                </span>
                <span
                  class="body2"
                  :data-testid="`tickets-page-anomaly-detection-time-row-${index}`"
                >
                  {{ getFormattedDateTime(item.originalTime, "h:mm A") }}
                </span>
              </div>
            </td>
            <td class="pa-4 ip-col">
              <div class="d-flex flex-column justify-center align-center text-center">
                <span
                  class="body2"
                  :data-testid="`tickets-page-anomaly-detection-country-row-${index}`"
                >
                  <span v-if="(item as AnomalyDetection).regionName"
                    >{{ (item as AnomalyDetection).regionName }},
                  </span>
                  {{ item.country }}
                </span>
                <span
                  class="body2"
                  :class="getThreatLevelColor((item as AnomalyDetection).threatLevel)"
                  :data-testid="`tickets-page-anomaly-detection-ip-row-${index}`"
                  >{{ item.ip }}</span
                >
              </div>
            </td>
          </tr>
        </template>
        <template #expanded-row="{ columns, item }">
          <tr v-if="isImpossibleTraveler">
            <td></td>
            <td :colspan="columns.length - 1" class="pa-4">
              <div v-if="(item as ImpossibleTravelerNormalized).isp" class="mb-4">
                <span class="subtitle2">{{
                  $t(`ticketDetails.fullDetails.anomalyDetection.orgName`)
                }}</span>
                <div class="body2">
                  {{ (item as ImpossibleTravelerNormalized).isp }}
                </div>
              </div>
              <div v-if="(item as ImpossibleTravelerNormalized).service" class="mb-4">
                <span class="subtitle2">{{
                  $t(`ticketDetails.fullDetails.impossibleTraveler.services`)
                }}</span>
                <div class="body2">
                  {{ $t("services." + (item as ImpossibleTravelerNormalized).service) }}
                </div>
              </div>
              <span class="subtitle2">{{
                $t(`ticketDetails.fullDetails.impossibleTraveler.travelDetails`)
              }}</span>
              <div class="body2">
                <span class="subtitle2">
                  {{ $t("ticketDetails.fullDetails.impossibleTraveler.from") }}
                  {{ (item as ImpossibleTravelerNormalized).previousLogin.countryName }}
                </span>
                ({{
                  getFormattedDateTime(
                    (item as ImpossibleTravelerNormalized).previousLogin.timestamp
                  )
                }})
                <span class="subtitle2">
                  {{ $t("ticketDetails.fullDetails.impossibleTraveler.to") }}
                  {{ (item as ImpossibleTravelerNormalized).currentLogin.countryName }}
                </span>
                ({{
                  getFormattedDateTime(
                    (item as ImpossibleTravelerNormalized).currentLogin.timestamp
                  )
                }})
                <div class="subtitle2">
                  {{ getTravelDuration(item as ImpossibleTravelerNormalized) }}
                </div>
              </div>
            </td>
          </tr>
          <tr v-else>
            <td></td>
            <td :colspan="columns.length - 1" class="pa-4">
              <div v-if="(item as AnomalyDetection).orgName">
                <span class="subtitle2">{{
                  $t(`ticketDetails.fullDetails.anomalyDetection.orgName`)
                }}</span>
                <div class="body2">
                  {{ (item as AnomalyDetection).orgName }}
                </div>
              </div>
              <div v-if="(item as AnomalyDetection).threatDetectionDate" class="mt-4">
                <span class="subtitle2">{{
                  $t(`ticketDetails.fullDetails.anomalyDetection.threatType`)
                }}</span>
                <div class="body2">
                  {{ getThreatType((item as AnomalyDetection).threatTypes) }}
                </div>
                <div class="caption">
                  {{ $t(`ticketDetails.fullDetails.anomalyDetection.lastDetected`) }}
                  {{
                    getFormattedDateTime(
                      (item as AnomalyDetection).threatDetectionDate,
                      "MMM DD, YYYY"
                    )
                  }}
                </div>
              </div>
              <div v-if="(item as AnomalyDetection).proxyType" class="mt-4">
                <span class="subtitle2">{{
                  $t(`ticketDetails.fullDetails.anomalyDetection.proxy`)
                }}</span>
                <div class="body2">
                  {{ (item as AnomalyDetection).proxyType }}
                </div>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table-server>
    </div>
  </div>
</template>

<script lang="ts">
import uniqueId from "lodash/uniqueId";
import { getFormattedDateTime } from "@/_helpers/date";
import { computed, type ComputedRef, defineComponent, type PropType, ref } from "vue";
import type {
  AnomalyDetection,
  ImpossibleTravelerDetails,
  TicketDetails,
} from "@/_store/tickets/tickets.module";
import { AnomalyIpThreatLevel, TicketTrigger, TicketType } from "@/constants/tickets";
import { getThreatLevelColor } from "@/_store/tickets/adapters";
import { useI18n } from "vue-i18n";
import moment from "moment";
import type { Service } from "@/constants/cloud-apps.ts";

export interface ImpossibleTravelerNormalized extends ImpossibleTravelerDetails {
  service: Service;
  type: string;
  originalTime: number;
  ip: string;
  isp: string;
  country: string;
}

export default defineComponent({
  props: {
    ticket: {
      type: Object as PropType<TicketDetails>,
      required: true,
    },
  },
  setup(props) {
    const i18n = useI18n();
    const expanded = ref([]);
    const singleExpand = ref(false);

    const impossibleTravelerRecords: ComputedRef<
      (ImpossibleTravelerNormalized & { uniqueId: string })[]
    > = computed(() => {
      if (isImpossibleTraveler.value) {
        return props.ticket.sections?.cloudThreatMetaData
          .map((record) => ({
            ...record,
            service: record.currentLogin.service,
            type: "login",
            originalTime: record.currentLogin.timestamp,
            ip: record.currentLogin.ip,
            isp: record.currentLogin.isp,
            country: record.currentLogin.countryName,
          }))
          .slice()
          .sort((a, b) => b.originalTime - a.originalTime)
          .map((item) => ({
            ...item,
            uniqueId: uniqueId(),
          }));
      }

      return [];
    });

    const records: ComputedRef<(AnomalyDetection & { uniqueId: string })[]> = computed(() => {
      return (
        props.ticket.sections?.anomalyDetectionMetaData?.records
          .slice()
          .sort((a, b) => b.originalTime - a.originalTime)
          .map((item) => ({
            ...item,
            uniqueId: uniqueId(),
          })) ?? []
      );
    });

    const shouldHaveExpander = (detailRecord: AnomalyDetection | ImpossibleTravelerDetails) => {
      const { orgName, threatDetectionDate, proxyType } = detailRecord as AnomalyDetection;

      return orgName || threatDetectionDate || proxyType || isImpossibleTraveler.value;
    };

    const isImpossibleTraveler = computed(
      () => props.ticket.eventTrigger === TicketTrigger.IMPOSSIBLE_TRAVELER
    );

    const getThreatType = (threatTypes: string[]) => threatTypes.join(", ");

    const getCircleStyle = (suspicious: boolean) => {
      if (suspicious || isImpossibleTraveler.value) {
        return {
          "background-color": "rgb(var(--v-theme-red-dark))",
          "border-color": "rgb(var(--v-theme-red-dark))",
        };
      }
      return {
        "background-color": "rgb(var(--v-theme-white))",
        "border-color": "rgb(var(--v-theme-green-light))",
      };
    };

    const getTravelDuration = (item: ImpossibleTravelerNormalized) => {
      let timeDifference: number | string = moment(item.currentLogin.timestamp).diff(
        moment(item.previousLogin.timestamp),
        "minutes"
      );

      if (timeDifference >= 60) {
        const hours = Math.floor(timeDifference / 60);
        const minutes = timeDifference - hours * 60;

        const minutesLabel = minutes < 10 ? `0${minutes}` : minutes;

        timeDifference = i18n.t("general.hours", {
          n: minutes > 0 ? `${hours}:${minutesLabel}` : hours,
        });
      } else {
        timeDifference = i18n.t("general.minutes", timeDifference);
      }

      return timeDifference;
    };

    return {
      getThreatLevelColor,
      records,
      AnomalyIpThreatLevel,
      expanded,
      singleExpand,
      TicketType,
      getFormattedDateTime,
      shouldHaveExpander,
      getThreatType,
      getCircleStyle,
      getTravelDuration,
      isImpossibleTraveler,
      impossibleTravelerRecords,
    };
  },
});
</script>

<style lang="scss" scoped>
.full-details-wrapper {
  border: 1px solid rgb(var(--v-theme-indigo-faint));
}

.ip-col {
  max-width: 40%;
  word-break: break-word;
}
</style>
